import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { Project } from "@/types/project";
import { getProjects } from "@/services/project-service";
import { Button } from "@/components/ui/button";
import ArrowRight from "@/assets/icons/ic-arrow-right.svg";
import ICPlus from "@/assets/icons/ic-plus-02.svg";
import IcRight from "@/assets/icons/ic-arr-right.svg";
import { STATUS_CONFIG } from "@/lib/utils";

export function ProjectCardsSection({ onLoadComplete }: { onLoadComplete?: () => void }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);

    const fetchProject = async () => {
      try {
        const res = await getProjects(1, 3);
        setProjects(res.data.data?.items || []);
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoading(false);
        onLoadComplete?.();
      }
    };
    fetchProject();
  }, []);

  if (isLoading) return null;

  return (
    <section className="flex flex-col gap-0.5 w-full">
      {/* Section Header */}
      <div className="flex items-center h-12 w-full lg:flex hidden">
        <div className="flex-1">
          <h3 className=" lg:block hidden text-xl font-bold leading-[28px] tracking-[-0.02em] text-[#000000]">
            내 프로젝트
          </h3>
        </div>
        {projects.length > 0 && (
          <button
            type="button"
            onClick={() => navigate("/projects", { state: { from: "my-page" } })}
            className="inline-flex items-center gap-2 text-base font-bold leading-6 tracking-[-0.01em] text-black  transition-opacity"
          >
            View all
            <img src={IcRight} alt="" className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Cards Grid */}
      {projects.length === 0 ? (
        <div className="flex flex-col items-center gap-5 md:pt-12 md:pb-[300px] pb-4 pt-10 border border-[rgba(0,0,0,0.15)]">
          <span className="text-base font-bold leading-6 tracking-[-0.01em] text-[#000000]">
            아직 생성된 프로젝트가 없습니다.
          </span>
          <button
            type="button"
            onClick={() => navigate("/chat")}
            className="inline-flex items-center gap-2 h-12 px-5 rounded-[4px] bg-[#FFAA72] text-[#000000] font-bold lg:text-base text-[14px] leading-6 tracking-[-0.01em] hover:opacity-80 transition-opacity"
          >
            <img src={ICPlus} className="lg:w-6 lg:h-6 w-5 h-5" alt="" />
            New chat
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 lg:flex-row lg:gap-6 w-full">
          {projects.map((project) => {
            const config = STATUS_CONFIG[project.status] || STATUS_CONFIG.init;
            const statusComplete = project.status === "completed";

            return (
              <div
                key={project.id}
                onClick={() =>
                  navigate(`/projects/${project.id}/report`, { state: { from: "list" } })
                }
                className="w-full h-[136px] md:w-[357px] md:h-[148px] flex-col gap-3 p-4 
                lg:pt-5 lg:pb-6 lg:px-6 pt-4 pb-4 px-4 rounded-none border border-[rgba(0,0,0,0.15)] 
                bg-white text-left cursor-pointer transition-shadow"
              >
                {/* Status Badge */}
                <span
                  className={`flex items-center lg:text-[16px] text-[14px] justify-center w-fit gap-1 py-[2px]  rounded-[4px] ${config.bg} ${config.text} ${config.padding}`}
                >
                  <span className="text-sm leading-5 tracking-[-0.01em]">{config.label}</span>
                </span>
                {/* Content */}
                <div className="flex flex-col gap-1 mt-3 w-full text-[#000000]">
                  <span className="text-base font-bold leading-6 tracking-[-0.01em] truncate">
                    {project.name || "Untitled"}
                  </span>
                  <span className="text-sm leading-5 tracking-[-0.01em] text-[#000000] line-clamp-2 h-10">
                    {project.description}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      {projects.length > 0 && (
        <Button
          variant="outline"
          onClick={() => navigate("/projects", { state: { from: "my-page" } })}
          className="lg:hidden mt-1 inline-flex items-center justify-center gap-2 w-full h-12 rounded-full border-none bg-white text-base font-bold leading-6 tracking-[-0.01em] text-[#000000] "
        >
          View all
          <img src={ArrowRight} alt="arrow-right" />
        </Button>
      )}
    </section>
  );
}
